import { useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';

export type StorageUsage = {
  total_assets: number;
  total_bytes_known: number;
  assets_with_size: number;
  by_kind: { kind: string; assets: number; bytes: number; sized: number }[];
  by_backend: { backend: string; assets: number; on_r2: number }[];
  by_bucket: { bucket: string; assets: number; bytes: number }[];
  weekly_uploads: { week: string; assets: number; bytes: number }[];
  top_owners: { owner: string; org_name: string | null; assets: number; bytes: number }[];
  deleted: { assets: number; bytes: number; still_on_uploadcare: number };
  listings_over_base_photo_limit: { eid: number; name: string | null; images: number; base_limit: number }[];
};

export type StorageHealth = {
  awaiting_uploadcare: number;
  uploadcare_purge_pending: number;
  no_location: number;
  quarantined: number;
  private: number;
  images: number;
  hashed: number;
  measured: number;
  vision_analysed: number;
  moderation: Record<string, number>;
  last_scan: null | { ajid: number; status: string; total: number; checked: number; finished_at: string | null; results: Record<string, number> };
};

export type StorageConfig = {
  storage_backend: string | null;
  dual_write: boolean | null;
  cdn_provider: string | null;
  uploadcare_upload: boolean | null;
  r2_migration_enabled: boolean | null;
  upload_caps: { image_mb: number; video_mb: number; min_image_px: (number | null)[]; min_profile_px: (number | null)[] };
  default_listing_limits: Record<string, number>;
  schedules: { name: string; every_min: number; does: string }[];
};

const unwrap = (raw: any) => (raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw ?? {});

const useView = <T,>(view: 'usage' | 'health' | 'config') => {
  const request = useClientStore((s) => s.request);
  const get = withErrorHandling(async (): Promise<T> => unwrap(await request(`/api/admin/assets/storage/${view}`)), `Could not load storage ${view}`);
  return useQuery({ queryKey: ['asset-storage', view], queryFn: get, staleTime: 60_000 });
};

export const useStorageUsageQuery = () => useView<StorageUsage>('usage');
export const useStorageHealthQuery = () => useView<StorageHealth>('health');
export const useStorageConfigQuery = () => useView<StorageConfig>('config');

export const gb = (bytes: number) => (bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(1)} GB` : `${Math.round(bytes / 1024 ** 2)} MB`);
